/**
 * Hook for fetching today's time tracking statistics
 * Returns total time logged, billable hours, and entry count
 */

import { useMemo } from 'react'
import { useAuth } from '@/components/auth/AuthProvider'
import { timeEntryApi } from '@/modules/time'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { TimeEntry, TimeEntryStatusEnum } from '@scrambl/shared'

import { useCalendarDay } from './useCalendarDay'
import { useUserTimezone } from './useUserTimezone'

export interface TodayTimeStats {
  totalMinutes: number
  billableMinutes: number
  nonBillableMinutes: number
  entriesCount: number
  draftEntriesCount: number
  submittedEntriesCount: number
  approvedEntriesCount: number
}

export interface UseTodayTimeStatsOptions {
  enabled?: boolean
  staleTime?: number
}

export interface UseTodayTimeStatsResult {
  stats: TodayTimeStats
  entries: TimeEntry[]
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  refresh: () => Promise<void>
}

// Calculate duration in minutes from a time entry
function getEntryDurationMinutes(entry: TimeEntry): number {
  // If entry has explicit duration (manual entry), use it
  if (entry.duration !== null && entry.duration !== undefined) {
    return Math.round(entry.duration * 60) // duration is in hours
  }

  // For timer entries, calculate from start/end time
  if (entry.startTime) {
    const start = new Date(entry.startTime)
    const end = entry.endTime ? new Date(entry.endTime) : new Date()
    let durationMs = end.getTime() - start.getTime()

    // Subtract paused duration if applicable
    if (entry.pausedDuration) {
      durationMs -= entry.pausedDuration * 1000 // pausedDuration is in seconds
    }

    return Math.max(0, Math.round(durationMs / (1000 * 60)))
  }

  return 0
}

export const TODAY_TIME_STATS_QUERY_KEY = ['today-time-stats'] as const

export function useTodayTimeStats(
  options: UseTodayTimeStatsOptions = {},
): UseTodayTimeStatsResult {
  const { enabled = true, staleTime = 30000 } = options

  const { user } = useAuth()
  const currentUserId = user?.id
  const currentOrganization = useOrganizationStore(
    state => state.currentOrganization,
  )
  const organizationId = currentOrganization?.id
  const isExternalUser = currentOrganization?.isExternalAccess === true
  const queryClient = useQueryClient()

  const { timezone } = useUserTimezone()
  const today = useCalendarDay(timezone)

  // Query for today's time entries
  const query = useQuery({
    queryKey: [
      ...TODAY_TIME_STATS_QUERY_KEY,
      organizationId,
      currentUserId,
      timezone,
      today,
      'display',
    ],
    queryFn: async () => {
      const response = await timeEntryApi.getAllTimeEntries({
        startDate: today,
        endDate: today,
        timezone,
        dateBasis: 'display',
      })

      return response.data || []
    },
    enabled: enabled && !!currentUserId && !!organizationId && !isExternalUser,
    staleTime,
  })

  // Calculate stats from entries
  const { stats, entries } = useMemo(() => {
    const entries = query.data || []

    // Filter to current user's entries and exclude canceled entries
    const userEntries = entries.filter(
      entry =>
        entry.userId === currentUserId &&
        entry.status !== TimeEntryStatusEnum.CANCELED,
    )

    let totalMinutes = 0
    let billableMinutes = 0
    let draftCount = 0
    let submittedCount = 0
    let approvedCount = 0

    userEntries.forEach(entry => {
      const duration = getEntryDurationMinutes(entry)
      totalMinutes += duration

      if (entry.billable) {
        billableMinutes += duration
      }

      switch (entry.status) {
        case TimeEntryStatusEnum.DRAFT:
          draftCount++
          break
        case TimeEntryStatusEnum.SUBMITTED:
          submittedCount++
          break
        case TimeEntryStatusEnum.APPROVED:
        case TimeEntryStatusEnum.COMPLETED:
          approvedCount++
          break
      }
    })

    const stats: TodayTimeStats = {
      totalMinutes,
      billableMinutes,
      nonBillableMinutes: totalMinutes - billableMinutes,
      entriesCount: userEntries.length,
      draftEntriesCount: draftCount,
      submittedEntriesCount: submittedCount,
      approvedEntriesCount: approvedCount,
    }

    return { stats, entries: userEntries }
  }, [query.data, currentUserId])

  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: TODAY_TIME_STATS_QUERY_KEY,
    })
  }

  return {
    stats,
    entries,
    // Pending counts as loading (waiting for the user/org or the persisted
    // cache is not fetching yet); external users never run this query.
    isLoading:
      query.isLoading || (enabled && !isExternalUser && query.isPending),
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refresh,
  }
}

// Helper to format minutes as hours and minutes string
export function formatDuration(totalMinutes: number): string {
  const normalizedMinutes = Math.round(totalMinutes)
  const hours = Math.floor(normalizedMinutes / 60)
  const minutes = normalizedMinutes % 60

  if (hours === 0) {
    return `${minutes}m`
  }

  if (minutes === 0) {
    return `${hours}h`
  }

  return `${hours}h ${minutes}m`
}
